/**
 * Cart Gamification - Actualización dinámica
 * Actualiza el componente de gamificación cuando cambia el carrito
 * Autor: Carlos Hernandez
 */

class ChCartGamification {
  constructor() {
    this.cartUpdateUnsubscriber = undefined;
    this.init();
  }

  init() {
    // Usar el sistema de eventos PUB_SUB si está disponible
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Esperar un poco para que el DOM se actualice
        setTimeout(() => this.updateGamification(), 100);
      });
    } else {
      // Fallback: escuchar cambios en el DOM del cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const observer = new MutationObserver(() => {
          this.updateGamification();
        });
        
        observer.observe(cartDrawer, {
          childList: true,
          subtree: true
        });
      }
    }

    // Actualizar cuando se carga la página
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.updateGamification(), 200);
      });
    } else {
      setTimeout(() => this.updateGamification(), 200);
    }
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  async updateGamification() {
    const progressElements = document.querySelectorAll('.ch-cart-gamification-progress');
    if (!progressElements.length) return;

    try {
      // Obtener el estado actual del carrito
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartTotal = cart.total_price;

      progressElements.forEach((element) => {
        const targetAmountBaseElement = parseInt(element.dataset.targetAmount || '400000');
        const targetAmountElement = targetAmountBaseElement * 100;
        let progressPercentageElement = (cartTotal / targetAmountElement) * 100;
        if (progressPercentageElement > 100) progressPercentageElement = 100;

        const step1AmountElement = targetAmountElement * 0.25;
        const step2AmountElement = targetAmountElement * 0.50;
        const step3AmountElement = targetAmountElement * 0.75;
        const step4AmountElement = targetAmountElement;

        let currentStepElement = 0;
        if (cartTotal >= step4AmountElement) {
          currentStepElement = 4;
        } else if (cartTotal >= step3AmountElement) {
          currentStepElement = 3;
        } else if (cartTotal >= step2AmountElement) {
          currentStepElement = 2;
        } else if (cartTotal >= step1AmountElement) {
          currentStepElement = 1;
        }

        const progressBar = element.querySelector('.ch-cart-gamification-progress__bar');
        if (progressBar) {
          progressBar.setAttribute('width', progressPercentageElement * 4);
        }

        // Solo actualizar el punto final (meta de envío gratis)
        const goalCircle = element.querySelector('.ch-cart-gamification-progress__step--goal');
        if (goalCircle) {
          if (currentStepElement >= 4) {
            const progressColor = element.dataset.progressColor || '#FFA500';
            goalCircle.setAttribute('fill', progressColor);
          } else {
            goalCircle.setAttribute('fill', '#E5E5E5');
          }
        }

        const truckIcon = element.querySelector('.ch-cart-gamification-truck-icon');
        if (truckIcon) {
          const truckX = Math.max(0, Math.min(progressPercentageElement * 4 - 18, 382));
          truckIcon.setAttribute('transform', `translate(${truckX}, 16) scale(0.06)`);
          const paths = truckIcon.querySelectorAll('path');
          const ellipses = truckIcon.querySelectorAll('ellipse');
          const polygons = truckIcon.querySelectorAll('polygon');
          [...paths, ...ellipses, ...polygons].forEach(element => {
            element.setAttribute('fill', '#000000');
          });
        }

        const messageText = element.querySelector('.ch-cart-gamification-progress__text');
        const productCountText = element.querySelector('.ch-cart-gamification-progress__product-count');
        const remainingText = element.querySelector('.ch-cart-gamification-progress__remaining');
      
      if (!messageText) {
        return;
      }
      
        const messages = {
          step0: element.dataset.messageStep0 || 'Agrega más productos para obtener envío gratis',
          step1: element.dataset.messageStep1 || '¡Sigue comprando! Estás en el camino correcto',
          step2: element.dataset.messageStep2 || '¡Vas por buen camino! Sigue agregando productos',
          step3: element.dataset.messageStep3 || '¡Estás muy cerca! Agrega más productos para envío gratis',
          step4: element.dataset.messageStep4 || '¡Felicidades! Has alcanzado el envío gratis'
        };

        const remaining = step4AmountElement - cartTotal;
        const remainingFormatted = this.formatMoney(remaining);
        const itemCount = cart.item_count || 0;
        const productText = itemCount === 1 ? 'producto' : 'productos';
        const selectedMessage = messages[`step${currentStepElement}`];

        if (productCountText) {
          productCountText.textContent = `Tienes ${itemCount} ${productText}`;
        }

        if (messageText) {
          messageText.textContent = selectedMessage;
        }

        if (remainingText) {
          if (currentStepElement < 4) {
            const isPage = element.classList.contains('ch-cart-gamification-progress--page');
            if (isPage) {
              const percentageRemaining = Math.ceil(remaining / targetAmountElement * 100);
              remainingText.textContent = `Estás a ${remainingFormatted} (${percentageRemaining}%) de obtener envío gratis`;
            } else {
              remainingText.textContent = `Te faltan ${remainingFormatted} para envío gratis`;
            }
            remainingText.style.display = 'block';
          } else {
            remainingText.style.display = 'none';
          }
        }

        element.dataset.cartTotal = cartTotal;
      });
      
    } catch (error) {
      console.error('Error actualizando gamificación del carrito:', error);
    }
  }

  formatMoney(cents) {
    // Convertir centavos a pesos
    const amount = cents / 100;
    
    // Formatear sin decimales cuando son cero
    const formatter = new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    return formatter.format(amount);
  }
}

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.chCartGamification = new ChCartGamification();
  });
} else {
  window.chCartGamification = new ChCartGamification();
}

